import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useRoleAccess } from '@/features/auth/hooks/useRoleAccess';
import { AuditInfo } from '../components/AuditInfo';
import { DeleteMunicipalListButton } from '../components/DeleteMunicipalListButton';
import { MunicipalListAccommodationsSection } from '../components/MunicipalListAccommodationsSection';
import { MunicipalListHeaderForm } from '../components/MunicipalListHeaderForm';
import { NotifyFiscalizacionButton } from '../components/NotifyFiscalizacionButton';
import { QueryStateMessage } from '../components/QueryStateMessage';
import { useMunicipalList } from '../hooks/useMunicipalList';
import { getApiErrorStatus } from '../utils/api-error.utils';
import { MUNICIPALITY_KIND_LABELS } from '../utils/format.utils';
import { PRE_REGISTRATION_EDIT_ROLES, preRegistrationRoutes } from '../utils/routes.utils';

// New list (no :listId) or an existing list: header + rows (master-detail)
export const MunicipalListFormPage: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const { hasRole } = useRoleAccess();
  const canEdit = hasRole(PRE_REGISTRATION_EDIT_ROLES);
  const { data: list, isLoading, isError, error, refetch } = useMunicipalList(listId);
  const notFound = getApiErrorStatus(error) === 404;

  const title = !listId
    ? 'Nuevo listado municipal'
    : list
      ? `Listado de ${list.municipality.name}`
      : 'Listado municipal';

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Button variant="ghost" size="sm" asChild className="-ml-3 mb-2">
              <Link to={preRegistrationRoutes.municipalLists}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Listados municipales
              </Link>
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
            {list && (
              <p className="mt-2 text-gray-600">
                {MUNICIPALITY_KIND_LABELS[list.municipality.kind]} ·{' '}
                {list.municipality.department.name} · {list.municipality.department.region.name}
              </p>
            )}
          </div>
          {list && canEdit && (
            <div className="flex flex-wrap gap-2">
              <NotifyFiscalizacionButton list={list} />
              <DeleteMunicipalListButton list={list} />
            </div>
          )}
        </div>

        {!listId ? (
          <Card>
            <CardHeader>
              <CardTitle>Datos del listado</CardTitle>
            </CardHeader>
            <CardContent>
              <MunicipalListHeaderForm />
            </CardContent>
          </Card>
        ) : (
          <QueryStateMessage
            isLoading={isLoading}
            isError={isError}
            isEmpty={false}
            errorMessage={
              notFound
                ? 'Listado municipal no encontrado.'
                : 'No se pudo cargar el listado municipal.'
            }
            emptyMessage=""
            onRetry={() => refetch()}
          >
            {list && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Datos del listado</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MunicipalListHeaderForm
                      list={list}
                      readOnly={!canEdit}
                      footer={
                        <AuditInfo
                          createdAt={list.createdAt}
                          updatedAt={list.updatedAt}
                          createdByUser={list.createdByUser}
                          updatedByUser={list.updatedByUser}
                        />
                      }
                    />
                  </CardContent>
                </Card>
                <MunicipalListAccommodationsSection list={list} canEdit={canEdit} />
              </div>
            )}
          </QueryStateMessage>
        )}
      </div>
    </MainLayout>
  );
};

export default MunicipalListFormPage;
